import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, ShieldAlert, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegisterPage = () => {
    const { register } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [broker, setBroker] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // Invitation states
    const [inviteCode, setInviteCode] = useState('');
    const [validatingInvite, setValidatingInvite] = useState(true);
    const [inviteValid, setInviteValid] = useState(false);
    const [inviteError, setInviteError] = useState('');

    useEffect(() => {
        const code = searchParams.get('invite');
        if (code) {
            setInviteCode(code);
            validateInvitation(code);
        } else {
            setValidatingInvite(false);
            setInviteError(language === 'id' 
                ? 'Link invitation diperlukan untuk mendaftar' 
                : 'Invitation link required to register'
            );
        }
    }, [searchParams]);

    const validateInvitation = async (code) => {
        setValidatingInvite(true);
        try {
            await axios.get(`${API}/auth/validate-invitation?code=${code}`);
            setInviteValid(true);
            setInviteError('');
        } catch (error) {
            setInviteValid(false);
            const detail = error.response?.data?.detail || 'Invalid invitation';
            setInviteError(detail);
        } finally {
            setValidatingInvite(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!inviteValid) {
            toast.error(language === 'id' ? 'Invitation tidak valid' : 'Invalid invitation');
            return;
        }
        
        if (!name || !email || !password || !confirmPassword) {
            toast.error(language === 'id' ? 'Mohon isi semua field' : 'Please fill all fields');
            return;
        }

        if (password !== confirmPassword) {
            toast.error(language === 'id' ? 'Password tidak cocok' : 'Passwords do not match');
            return;
        }

        if (password.length < 6) {
            toast.error(language === 'id' ? 'Password minimal 6 karakter' : 'Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            await register(name, email, password, inviteCode, broker);
            toast.success(language === 'id' ? 'Registrasi berhasil!' : 'Registration successful!');
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || (language === 'id' ? 'Registrasi gagal' : 'Registration failed');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // Show loading while validating
    if (validatingInvite) {
        return (
            <div className="min-h-screen flex flex-col relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ 
                        backgroundImage: 'url(/assets/background-logo.jpg)',
                        filter: 'blur(2px)'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-800/80 to-red-900/70" />
                <div className="relative z-10 min-h-screen flex items-center justify-center">
                    <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
                        <CardContent className="py-12 text-center">
                            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-4" />
                            <p className="text-slate-600">
                                {language === 'id' ? 'Memvalidasi invitation...' : 'Validating invitation...'}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Show error if no valid invitation
    if (!inviteValid) {
        return (
            <div className="min-h-screen flex flex-col relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ 
                        backgroundImage: 'url(/assets/background-logo.jpg)',
                        filter: 'blur(2px)'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-800/80 to-red-900/70" />
                
                <div className="relative z-10 min-h-screen flex flex-col">
                    <header className="p-4 flex justify-between items-center">
                        <Link to="/" className="flex items-center gap-3">
                            <img 
                                src="/assets/logo.jpg" 
                                alt="Sekolah NusaTraders" 
                                className="w-12 h-12 rounded-lg object-contain shadow-lg"
                            />
                            <div>
                                <span className="font-heading font-bold text-lg text-white block leading-tight drop-shadow-lg">SEKOLAH</span>
                                <span className="font-heading font-bold text-sm text-red-400 block leading-tight drop-shadow-lg">NUSATRADERS</span>
                            </div>
                        </Link>
                    </header>
                    
                    <main className="flex-1 flex items-center justify-center p-4">
                        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
                            <CardContent className="py-12 text-center">
                                <ShieldAlert className="w-16 h-16 mx-auto text-red-500 mb-4" />
                                <h2 className="text-xl font-heading font-bold text-slate-800 mb-2">
                                    {language === 'id' ? 'Akses Ditolak' : 'Access Denied'}
                                </h2>
                                <p className="text-slate-600 mb-6">
                                    {inviteError}
                                </p>
                                <div className="space-y-3">
                                    <p className="text-sm text-slate-500">
                                        {language === 'id' 
                                            ? 'Hubungi admin untuk mendapatkan link invitation'
                                            : 'Contact admin to get an invitation link'
                                        }
                                    </p>
                                    <Link to="/login">
                                        <Button variant="outline" className="w-full">
                                            {language === 'id' ? 'Sudah punya akun? Login' : 'Already have account? Login'}
                                        </Button>
                                    </Link>
                                </div>
                            </CardContent>
                        </Card>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col relative overflow-hidden">
            {/* Background Image with Overlay */}
            <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ 
                    backgroundImage: 'url(/assets/background-logo.jpg)',
                    filter: 'blur(2px)'
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-800/80 to-red-900/70" />
            
            {/* Content */}
            <div className="relative z-10 min-h-screen flex flex-col">
                {/* Header */}
                <header className="p-4 flex justify-between items-center">
                    <Link to="/" className="flex items-center gap-3">
                        <img 
                            src="/assets/logo.jpg" 
                            alt="Sekolah NusaTraders" 
                            className="w-12 h-12 rounded-lg object-contain shadow-lg"
                        />
                        <div>
                            <span className="font-heading font-bold text-lg text-white block leading-tight drop-shadow-lg">SEKOLAH</span>
                            <span className="font-heading font-bold text-sm text-red-400 block leading-tight drop-shadow-lg">NUSATRADERS</span>
                        </div>
                    </Link>
                    <div className="lang-toggle" data-testid="lang-toggle">
                        <button
                            onClick={() => language !== 'id' && toggleLanguage()}
                            className={language === 'id' ? 'active' : ''}
                        >
                            ID
                        </button>
                        <button
                            onClick={() => language !== 'en' && toggleLanguage()}
                            className={language === 'en' ? 'active' : ''}
                        >
                            EN
                        </button>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
                    <CardHeader className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <ShieldCheck className="w-5 h-5 text-emerald-500" />
                            <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-1 rounded">
                                {language === 'id' ? 'Invitation Valid' : 'Valid Invitation'}
                            </span>
                        </div>
                        <CardTitle className="text-2xl font-heading">
                            {language === 'id' ? 'Buat Akun Baru' : 'Create New Account'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'id' ? 'Daftar untuk akses VVIP' : 'Register for VVIP access'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">{t('name')}</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder={language === 'id' ? 'Nama lengkap' : 'Full name'}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    disabled={loading}
                                    data-testid="register-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('email')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="email@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    data-testid="register-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">{t('password')}</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        data-testid="register-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">
                                    {language === 'id' ? 'Konfirmasi Password' : 'Confirm Password'}
                                </Label>
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={loading}
                                    data-testid="register-confirm-password"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="broker">
                                    {language === 'id' ? 'Broker yang digunakan sekarang' : 'Current broker used'}
                                </Label>
                                <Input
                                    id="broker"
                                    type="text"
                                    placeholder={language === 'id' ? 'Contoh: XM, Exness, IC Markets' : 'Example: XM, Exness, IC Markets'}
                                    value={broker}
                                    onChange={(e) => setBroker(e.target.value)}
                                    disabled={loading}
                                    data-testid="register-broker"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                                data-testid="register-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t('loading')}
                                    </>
                                ) : (
                                    t('register')
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm">
                            <span className="text-slate-500">{t('haveAccount')}</span>{' '}
                            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                                {t('login')}
                            </Link>
                        </div>
                    </CardContent>
                    </Card>
                </main>
            </div>
        </div>
    );
};
